import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import { consumeFragmentToken, verifyEmail } from "../../api/planner";
import { Button } from "../../components/Button";
import { LoadingState } from "../../components/LoadingState";

const INVALID_TOKEN_CODES = new Set(["invalid_token", "token_invalid", "token_expired", "token_replayed", "access_token_invalid"]);

export function VerifyPage({ navigate, announce = () => undefined }: { navigate: (path: string, replace?: boolean) => void; announce?: (message: string) => void }) {
  const started = useRef(false);
  const inFlight = useRef(false);
  const [token] = useState(() => consumeFragmentToken());
  const [state, setState] = useState<"loading" | "invalid" | "transient">(() => token ? "loading" : "invalid");
  const [retryMessage, setRetryMessage] = useState("");
  const [autoRetrySafe, setAutoRetrySafe] = useState(false);

  const attemptVerification = useCallback(async () => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    setState("loading");
    setRetryMessage("");
    setAutoRetrySafe(false);
    try {
      const result = await verifyEmail(token);
      const publicId = result.public_id ?? result.plan_id;
      if (!publicId) {
        inFlight.current = false;
        setState("invalid");
        return;
      }
      inFlight.current = false;
      announce("Email verified");
      navigate(`/plans/${encodeURIComponent(publicId)}`, true);
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      if (apiError && (apiError.status === 410 || INVALID_TOKEN_CODES.has(apiError.code))) {
        inFlight.current = false;
        setState("invalid");
        return;
      }
      // Release the request guard before exposing retry controls or registering
      // the online listener. Otherwise an online event in that render can race
      // the finally block and be silently discarded.
      inFlight.current = false;
      setState("transient");
      setRetryMessage(apiError?.message ?? "We couldn’t verify this link right now. Try again.");
      setAutoRetrySafe(apiError?.code === "offline" || apiError?.code === "csrf_unavailable");
    }
  }, [announce, navigate, token]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void attemptVerification();
  }, [attemptVerification]);

  useEffect(() => {
    if (state !== "transient" || !autoRetrySafe) return;
    const retryWhenOnline = () => { void attemptVerification(); };
    window.addEventListener("online", retryWhenOnline);
    return () => window.removeEventListener("online", retryWhenOnline);
  }, [attemptVerification, autoRetrySafe, state]);

  if (state === "loading") return <LoadingState label="Verifying your secure link" />;
  if (state === "transient") return (
    <section className="boundary-card page--narrow" role="alert">
      <p className="eyebrow">Verification interrupted</p>
      <h1>We couldn’t verify your secure link yet</h1>
      <p>{retryMessage}</p>
      <p>The secure token remains only in this open page. It has been removed from the address and was not saved in browser storage.</p>
      <div className="action-row">
        <Button onClick={() => { void attemptVerification(); }}>Retry verification</Button>
        <Button variant="secondary" onClick={() => navigate("/access/recovery")}>Request a new secure link</Button>
      </div>
    </section>
  );
  return (
    <section className="boundary-card page--narrow" role="alert">
      <p className="eyebrow">Secure link unavailable</p>
      <h1>This secure link has expired or is no longer valid</h1>
      <p>For privacy, we cannot say whether a saved plan exists. You can request a fresh link safely.</p>
      <Button onClick={() => navigate("/access/recovery")}>Request a new secure link</Button>
    </section>
  );
}
